/**
 * Carte des Loyers (ANIL / DHUP) — loyer médian d'annonce par commune.
 *
 * Source : dataset data.gouv.fr "Carte des loyers" (millésime 2025).
 * Valeurs en €/m²/mois charges comprises, pour un bien de référence
 * (~52 m² pour les apparts, par typologie T1/T2 et T3+). Cf.
 * `functions/src/getLoyers.ts` pour les détails upstream.
 *
 * Pourquoi une callable backend plutôt qu'un fetch client direct ?
 *   - Les 4 CSV pèsent ~18 Mo cumulés pour ne lire qu'UNE ligne côté client.
 *   - Cache `/communes/{insee}/loyers/current` partagé entre tous les
 *     utilisateurs d'une même commune — une commune nouvellement consultée
 *     coûte ~2 s, les suivantes ~50 ms.
 *   - Libère le navigateur des contraintes CORS et de la gestion
 *     d'encodage Latin-1 des CSV français.
 *
 * Côté client, on reste sur le pattern `useEnvBlock` pour mutualiser le
 * cache fiche avec les autres cartes env (écoles, commerces, …). La clé
 * d'entrée est l'INSEE commune uniquement — pas de lat/lng, donc toutes
 * les parcelles d'une même commune partagent la même donnée.
 */

import { useMemo } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useEnvBlock } from "./shared";
import type { FicheDoc } from "../fiches";

// ─── Types (keep in sync with functions/src/getLoyers.ts) ────────────────

export type LoyerIndicator = {
  loyerM2: number;
  lowerM2: number;
  upperM2: number;
  typpred: string;
  nbobsCommune: number;
  nbobsMaille: number;
};

export type LoyersData = {
  insee: string;
  commune: string;
  appartement: LoyerIndicator | null;
  appT12: LoyerIndicator | null;
  appT3plus: LoyerIndicator | null;
  maison: LoyerIndicator | null;
  millesime: string;
  r2Adj: number | null;
  cachedAt: string;
};

// TTL client : 180 jours, aligné sur la cadence du millésime annuel ANIL.
// Le backend applique le même TTL — les deux étages ne se chevauchent pas
// inutilement.
const LOYERS_TTL_DAYS = 180;

const callLoyers = httpsCallable<{ insee: string }, LoyersData>(
  functions,
  "getLoyers",
);

// ─── Fetcher ─────────────────────────────────────────────────────────────

export async function fetchLoyers(insee: string): Promise<LoyersData | null> {
  try {
    const res = await callLoyers({ insee });
    return res.data;
  } catch (e) {
    // Commune absente du dataset (Mayotte, quelques îlots) → null, pas une
    // erreur. Le hook `useEnvBlock` persistera `data: null` et la card
    // rendra un état vide calme.
    const code = (e as { code?: string } | null)?.code ?? "";
    if (code === "functions/not-found" || code === "not-found") return null;
    throw e;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useLoyers(fiche: FicheDoc | null) {
  const opts = useMemo(() => {
    if (!fiche?.id || !fiche.geocode?.insee) return null;
    const insee = fiche.geocode.insee;
    return {
      ficheId: fiche.id,
      blockId: "loyers" as const,
      // v1|INSEE. Une édition annuelle du dataset suffit à invalider via
      // le TTL ; pas besoin de tagger le millésime dans la clé (le backend
      // sert toujours la version la plus récente via l'URL "latest").
      inputsKey: `v1|${insee}`,
      ttlDays: LOYERS_TTL_DAYS,
      fetcher: () => fetchLoyers(insee),
    };
  }, [fiche?.id, fiche?.geocode?.insee]);

  return useEnvBlock<LoyersData | null>(opts);
}

// ─── Selectors ───────────────────────────────────────────────────────────

/**
 * Choisit le bon indicateur de loyer pour un type de bien et une surface.
 *
 * Règle métier :
 *   - Maison → `maison`.
 *   - Appartement avec surface < 45 m² → `appT12` (T1/T2 typique).
 *   - Appartement avec surface ≥ 45 m² → `appT3plus`.
 *   - Appartement sans surface renseignée → `appartement` (tous types,
 *     c'est le plus dense).
 *   - Immeuble → `appartement` (pas de vraie typologie — on approxime par
 *     le loyer appart moyen de la commune).
 *   - Terrain → null (pas de loyer sur un terrain nu).
 *
 * Fallback en cascade : si la typologie préférée est absente (petit village
 * sans assez d'observations T1/T2), on retombe sur l'indicateur apparts
 * global, puis sur `maison`.
 */
export function selectLoyerFor(
  data: LoyersData,
  propertyType: "Maison" | "Appartement" | "Immeuble" | "Terrain" | null,
  surfaceM2: number | null,
): { indicator: LoyerIndicator; basis: "maison" | "appT12" | "appT3plus" | "appartement" } | null {
  if (propertyType === "Terrain") return null;

  if (propertyType === "Maison") {
    if (data.maison) return { indicator: data.maison, basis: "maison" };
    // Très rare : petite commune sans data maison mais avec apparts.
    if (data.appartement) return { indicator: data.appartement, basis: "appartement" };
    return null;
  }

  // Appartement / Immeuble → on pilote sur la surface.
  if (propertyType === "Appartement" && surfaceM2 != null) {
    if (surfaceM2 < 45 && data.appT12) {
      return { indicator: data.appT12, basis: "appT12" };
    }
    if (surfaceM2 >= 45 && data.appT3plus) {
      return { indicator: data.appT3plus, basis: "appT3plus" };
    }
  }
  if (data.appartement) return { indicator: data.appartement, basis: "appartement" };
  // Dernier recours pour un immeuble/appart en village : utiliser la
  // médiane maison, moins faussée par quelques annonces premium.
  if (data.maison) return { indicator: data.maison, basis: "maison" };
  return null;
}

/**
 * Rendement brut potentiel = (loyer mensuel × 12) / prix de vente, en %.
 *
 * "Brut" au sens notarial : aucun abattement (charges, taxe foncière,
 * vacance, travaux). C'est délibéré — le rendement brut est la métrique
 * de comparaison standard dans l'immobilier français, le net n'est
 * calculable qu'avec des hypothèses spécifiques au projet.
 *
 * Renvoie null si l'un des inputs est manquant ou non positif.
 */
export function computeRendementBrutPct(
  loyerM2: number,
  surfaceM2: number,
  priceEur: number,
): number | null {
  if (!Number.isFinite(loyerM2) || loyerM2 <= 0) return null;
  if (!Number.isFinite(surfaceM2) || surfaceM2 <= 0) return null;
  if (!Number.isFinite(priceEur) || priceEur <= 0) return null;
  const monthlyRent = loyerM2 * surfaceM2;
  const annualRent = monthlyRent * 12;
  return (annualRent / priceEur) * 100;
}
